<template>
  <div>
    <v-container fluid>
      <base-material-card
        icon="mdi-calculator-variant"
        title="ประมวลผลการเลื่อนค่าตอบแทนพนักงานราชการ (ส่วนกลาง)"
        class="px-5 py-3"
      >
        <v-alert
          type="info"
          outlined
          border="left"
          colored-border
          elevation="1"
          icon="mdi-information-outline"
          class="mb-4"
        >
          <div class="font-weight-bold">
            ส่วนกลางประมวลผลแทนสถานศึกษาทีละแห่งแบบอัตโนมัติ
            ด้วยหลักการเดียวกับเมนู "คำนวณค่าตอบแทน" ของสถานศึกษา
          </div>
          <div class="caption mt-1">
            วงเงินรวมไม่เกิน <strong>4%</strong> ของฐานค่าตอบแทน ×
            12 เดือน (ข้อ 1(3)) · รายบุคคลไม่เกิน <strong>6%</strong> (ข้อ
            1(2)) · ปัดเศษไม่ถึง 10 บาทเป็น 10 บาท (ข้อ 1(6)) · มีผล
            <strong>1 ตุลาคม</strong> · ผลที่ได้เป็น snapshot
            ณ เวลาประมวลผล ไม่แก้ไขค่าตอบแทนในทะเบียนประวัติ
          </div>
        </v-alert>

        <!-- ===== STEP 1: ปี + รอบการประมวลผล ===== -->
        <v-card outlined class="mb-4">
          <v-card-title class="subtitle-1 font-weight-bold grey lighten-4 py-2">
            <v-avatar color="info darken-3" size="26" class="mr-2">
              <span class="white--text caption font-weight-bold">1</span>
            </v-avatar>
            เลือกปีและรอบการประมวลผล
          </v-card-title>
          <v-card-text class="pt-4">
            <v-row dense>
              <v-col cols="12" md="3">
                <v-select
                  v-model="compYear"
                  :items="years"
                  label="ปีงบประมาณที่ประเมิน"
                  outlined
                  dense
                  prepend-inner-icon="mdi-calendar-range"
                  :hint="`เลื่อนค่าตอบแทน 1 ต.ค. ${compYear}`"
                  persistent-hint
                  @change="loadOverview"
                />
              </v-col>
              <v-col cols="12" md="9">
                <div v-if="overview" class="d-flex flex-wrap" style="gap:8px">
                  <v-chip small outlined color="primary">
                    <v-icon small left>mdi-account-group</v-icon>
                    พนักงานราชการ
                    {{ fmt(overview.population.personnel) }} คน /
                    {{ fmt(overview.population.colleges) }} แห่ง
                  </v-chip>
                  <v-chip
                    v-for="r in [1, 2]"
                    :key="r"
                    small
                    outlined
                    :color="roundStat(r).total > 0 ? 'success' : 'grey'"
                  >
                    <v-icon small left>mdi-clipboard-check-outline</v-icon>
                    ผลประเมินรอบ {{ r }}:
                    {{ fmt(roundStat(r).total) }} คน
                    (อนุมัติ {{ fmt(roundStat(r).approved) }}) /
                    {{ fmt(roundStat(r).colleges) }} แห่ง
                  </v-chip>
                  <v-chip
                    small
                    outlined
                    :color="overview.attendance.total > 0 ? 'success' : 'warning'"
                  >
                    <v-icon small left>mdi-calendar-account</v-icon>
                    ข้อมูลการลา {{ fmt(overview.attendance.total) }} คน /
                    {{ fmt(overview.attendance.colleges) }} แห่ง
                  </v-chip>
                </div>
                <div
                  v-if="overview && overview.attendance.total < overview.population.personnel / 2"
                  class="caption warning--text mt-2"
                >
                  <v-icon x-small color="warning">mdi-alert</v-icon>
                  ข้อมูลการลา/สาย/ขาดยังบันทึกไม่ครบ — ผู้ไม่มีข้อมูลจะถือว่าไม่มีวันลา
                  (ตรวจสิทธิ์ข้อ 1(7)-(9) ได้เฉพาะผู้ที่มีข้อมูล)
                </div>
              </v-col>
            </v-row>

            <!-- รายการรอบ -->
            <v-data-table
              :headers="runHeaders"
              :items="runs"
              dense
              hide-default-footer
              disable-pagination
              class="mt-3 elevation-0 run-table"
              no-data-text="ยังไม่มีรอบการประมวลผลในปีนี้ — สร้างรอบใหม่ด้านล่าง"
              :item-class="r => (currentRun && r.run_id === currentRun.run_id ? 'blue lighten-5' : '')"
            >
              <template v-slot:item.run_id="{ item }">#{{ item.run_id }}</template>
              <template v-slot:item.round_mode="{ item }">
                {{ roundModeText(item.round_mode) }}
              </template>
              <template v-slot:item.salary_source="{ item }">
                {{ salarySourceText(item.salary_source) }}
              </template>
              <template v-slot:item.run_status="{ item }">
                <v-chip x-small dark :color="item.run_status === 'final' ? 'success' : 'grey'">
                  {{ item.run_status === "final" ? "ยืนยันผลแล้ว" : "ฉบับร่าง" }}
                </v-chip>
              </template>
              <template v-slot:item.processed_colleges="{ item }">
                {{ fmt(item.processed_colleges) }} /
                {{ overview ? fmt(overview.population.colleges) : "-" }}
              </template>
              <template v-slot:item.created_at="{ item }">
                <div class="caption">{{ item.created_at }}</div>
                <div class="caption grey--text">{{ item.created_by_name }}</div>
              </template>
              <template v-slot:item.actions="{ item }">
                <v-btn x-small color="primary" class="mr-1" @click="selectRun(item)">
                  <v-icon x-small left>mdi-folder-open</v-icon>เปิด
                </v-btn>
                <v-btn
                  v-if="item.run_status === 'draft'"
                  x-small
                  icon
                  color="error"
                  @click="deleteRun(item)"
                >
                  <v-icon small>mdi-delete</v-icon>
                </v-btn>
              </template>
            </v-data-table>

            <v-divider class="my-3" />
            <v-row dense align="center">
              <v-col cols="12" md="3">
                <v-select
                  v-model="newRun.round_mode"
                  :items="roundModeItems"
                  label="รอบผลประเมินที่ใช้"
                  outlined
                  dense
                  hide-details
                />
              </v-col>
              <v-col cols="12" md="3">
                <v-select
                  v-model="newRun.salary_source"
                  :items="salarySourceItems"
                  label="ฐานค่าตอบแทน"
                  outlined
                  dense
                  hide-details
                />
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field
                  v-model="newRun.run_note"
                  label="หมายเหตุ (ถ้ามี)"
                  outlined
                  dense
                  hide-details
                  maxlength="255"
                />
              </v-col>
              <v-col cols="12" md="2">
                <v-btn block color="info darken-3" dark :loading="busy" @click="createRun">
                  <v-icon left>mdi-plus</v-icon>สร้างรอบใหม่
                </v-btn>
              </v-col>
            </v-row>
            <div class="caption grey--text mt-2">
              สถานศึกษาใช้ฐานจากทะเบียนประวัติ (personnel_temporary)
              และผลประเมินรอบที่เลือก (ค่าเริ่มต้นของสถานศึกษาคือรอบ 2)
              — "รอบล่าสุด" ใช้รอบ 2 ถ้ามี ไม่มีใช้รอบ 1
            </div>
          </v-card-text>
        </v-card>

        <template v-if="currentRun">
          <!-- ===== STEP 2: ประมวลผลทีละสถานศึกษา ===== -->
          <v-card outlined class="mb-4">
            <v-card-title class="subtitle-1 font-weight-bold grey lighten-4 py-2">
              <v-avatar color="info darken-3" size="26" class="mr-2">
                <span class="white--text caption font-weight-bold">2</span>
              </v-avatar>
              ประมวลผลรอบ #{{ currentRun.run_id }}
              <span class="caption ml-2 grey--text">
                ({{ roundModeText(currentRun.round_mode) }} ·
                {{ salarySourceText(currentRun.salary_source) }} ·
                วงเงิน {{ currentRun.budget_pct }}% · สูงสุด {{ currentRun.max_pct }}%)
              </span>
              <v-spacer />
              <v-chip v-if="isFinal" small dark color="success">
                <v-icon small left>mdi-lock</v-icon>ยืนยันผลแล้ว
                {{ currentRun.finalized_at }}
              </v-chip>
            </v-card-title>
            <v-card-text class="pt-4">
              <v-row dense align="center">
                <v-col cols="12" md="7">
                  <v-progress-linear
                    :value="progressPct"
                    height="24"
                    rounded
                    :color="errorCount ? 'warning' : 'success'"
                  >
                    <span class="caption white--text font-weight-bold">
                      ประมวลผลแล้ว {{ fmt(doneCount) }} / {{ fmt(colleges.length) }} แห่ง
                      ({{ progressPct.toFixed(1) }}%)
                      <span v-if="errorCount"> · ผิดพลาด {{ errorCount }}</span>
                    </span>
                  </v-progress-linear>
                </v-col>
                <v-col cols="12" md="5" class="text-right">
                  <v-btn
                    v-if="!processing"
                    color="primary"
                    small
                    class="mr-1"
                    :disabled="isFinal || !colleges.length"
                    @click="processColleges(colleges)"
                  >
                    <v-icon small left>mdi-play</v-icon>ประมวลผลทุกสถานศึกษา
                  </v-btn>
                  <v-btn
                    v-if="!processing"
                    color="warning darken-1"
                    small
                    class="mr-1"
                    :disabled="isFinal || pendingOrErrorCount === 0"
                    @click="processColleges(colleges.filter(c => c.state !== 'ok'))"
                  >
                    <v-icon small left>mdi-replay</v-icon>เฉพาะที่ค้าง/ผิดพลาด
                    ({{ pendingOrErrorCount }})
                  </v-btn>
                  <v-btn v-if="processing" color="error" small @click="stopRequested = true">
                    <v-icon small left>mdi-stop</v-icon>หยุด
                  </v-btn>
                </v-col>
              </v-row>

              <v-row dense class="mt-2">
                <v-col cols="12" md="4">
                  <v-text-field
                    v-model="collegeSearch"
                    dense
                    outlined
                    hide-details
                    clearable
                    prepend-inner-icon="mdi-magnify"
                    label="ค้นหาสถานศึกษา / จังหวัด / รหัส"
                  />
                </v-col>
                <v-col cols="12" md="8" class="d-flex align-center">
                  <v-btn-toggle v-model="collegeFilter" dense mandatory>
                    <v-btn x-small value="all">ทั้งหมด</v-btn>
                    <v-btn x-small value="pending">ยังไม่ประมวลผล</v-btn>
                    <v-btn x-small value="error">ผิดพลาด</v-btn>
                    <v-btn x-small value="incomplete">ข้อมูลไม่ครบ</v-btn>
                  </v-btn-toggle>
                </v-col>
              </v-row>

              <v-data-table
                :headers="collegeHeaders"
                :items="filteredColleges"
                :search="collegeSearch"
                dense
                class="mt-2 elevation-0"
                :footer-props="{ itemsPerPageOptions: [20, 50, 100, -1] }"
                :items-per-page="20"
                item-key="college_code"
              >
                <template v-slot:item.college_name="{ item }">
                  <div class="font-weight-medium">{{ item.college_name }}</div>
                  <div class="caption grey--text">
                    {{ item.college_code }} · {{ item.province_name || "-" }}
                  </div>
                </template>
                <template v-slot:item.state="{ item }">
                  <v-chip x-small dark :color="stateColor(item.state)">
                    <v-progress-circular
                      v-if="item.state === 'processing'"
                      indeterminate
                      size="10"
                      width="2"
                      class="mr-1"
                    />
                    {{ stateText(item.state) }}
                  </v-chip>
                  <div
                    v-if="item.process_message"
                    class="caption"
                    :class="item.state === 'error' ? 'error--text' : 'grey--text'"
                    style="max-width:220px;white-space:normal"
                  >
                    {{ item.process_message }}
                  </div>
                </template>
                <template v-slot:item.eligible_count="{ item }">
                  <span v-if="item.state === 'ok'">
                    {{ fmt(item.eligible_count) }} / {{ fmt(item.personnel_count) }}
                  </span>
                  <span v-else class="grey--text">- / {{ fmt(item.personnel) }}</span>
                </template>
                <template v-slot:item.budget_year="{ item }">
                  {{ item.state === "ok" ? fmt(item.budget_year) : "-" }}
                </template>
                <template v-slot:item.distributed_year="{ item }">
                  <span v-if="item.state === 'ok'" class="font-weight-bold primary--text">
                    {{ fmt(item.distributed_year) }}
                  </span>
                  <span v-else>-</span>
                </template>
                <template v-slot:item.actions="{ item }">
                  <v-btn
                    icon
                    x-small
                    color="info"
                    :disabled="item.state !== 'ok'"
                    @click="openCollegeDetail(item)"
                  >
                    <v-icon small>mdi-eye</v-icon>
                  </v-btn>
                  <v-btn
                    icon
                    x-small
                    color="primary"
                    :disabled="isFinal || processing"
                    @click="processColleges([item])"
                  >
                    <v-icon small>mdi-refresh</v-icon>
                  </v-btn>
                </template>
              </v-data-table>
            </v-card-text>
          </v-card>

          <!-- ===== STEP 3: รายงานสรุป ===== -->
          <v-card outlined class="mb-4" v-if="summary">
            <v-card-title class="subtitle-1 font-weight-bold grey lighten-4 py-2">
              <v-avatar color="info darken-3" size="26" class="mr-2">
                <span class="white--text caption font-weight-bold">3</span>
              </v-avatar>
              รายงานสรุปการเลื่อนค่าตอบแทน (เสนอสำนักงบประมาณ)
              <v-spacer />
              <v-btn small outlined color="success" class="mr-1" :loading="exporting" @click="exportExcel">
                <v-icon small left>mdi-microsoft-excel</v-icon>ส่งออก Excel
              </v-btn>
              <v-btn small outlined color="primary" class="mr-1" @click="printReport">
                <v-icon small left>mdi-printer</v-icon>พิมพ์รายงาน
              </v-btn>
              <v-btn
                v-if="!isFinal"
                small
                color="success"
                :disabled="processing || totals.processed < summary.expected_colleges"
                @click="finalizeRun"
              >
                <v-icon small left>mdi-lock-check</v-icon>ยืนยันผล
              </v-btn>
              <v-btn v-else small outlined color="warning darken-2" @click="reopenRun">
                <v-icon small left>mdi-lock-open-variant</v-icon>ยกเลิกการยืนยัน
              </v-btn>
            </v-card-title>
            <v-card-text class="pt-4">
              <v-alert
                v-if="totals.processed < summary.expected_colleges"
                type="warning"
                dense
                outlined
                class="mb-3"
              >
                ประมวลผลแล้ว {{ fmt(totals.processed) }} จาก
                {{ fmt(summary.expected_colleges) }} แห่ง —
                ยอดรวมด้านล่างยังไม่ครบทุกสถานศึกษา
              </v-alert>

              <v-row dense>
                <v-col v-for="k in kpis" :key="k.label" cols="6" md="3">
                  <div class="kpi" :style="{ borderLeftColor: k.color }">
                    <div class="kpi-label">{{ k.label }}</div>
                    <div class="kpi-value" :style="{ color: k.color }">{{ k.value }}</div>
                    <div class="kpi-unit">{{ k.unit }}</div>
                  </div>
                </v-col>
              </v-row>

              <v-progress-linear
                class="mt-3"
                :value="Math.min(budgetUsePct, 100)"
                height="20"
                rounded
                :color="budgetUsePct > 100 ? 'error' : 'success'"
              >
                <span class="caption white--text font-weight-bold">
                  ใช้วงเงิน {{ budgetUsePct.toFixed(2) }}%
                  ({{ fmt(totals.distributed_year) }} / {{ fmt(totals.budget_year) }} บาท/ปี)
                </span>
              </v-progress-linear>

              <!-- ความครบถ้วนของข้อมูล -->
              <div class="subtitle-2 font-weight-bold mt-5 mb-2">
                <v-icon small>mdi-database-check</v-icon> ความครบถ้วนของข้อมูล
              </div>
              <v-row dense>
                <v-col v-for="q in quality" :key="q.label" cols="6" md="2">
                  <v-card outlined class="pa-2 text-center" :class="q.value > 0 ? q.bg : ''">
                    <div class="title" :class="q.value > 0 ? q.color : 'grey--text'">
                      {{ fmt(q.value) }}
                    </div>
                    <div class="caption">{{ q.label }}</div>
                    <div class="caption grey--text">{{ q.hint }}</div>
                  </v-card>
                </v-col>
              </v-row>

              <v-row class="mt-3">
                <v-col cols="12" md="7">
                  <div class="subtitle-2 font-weight-bold mb-1">สรุปรายภาค</div>
                  <v-simple-table dense class="report-table">
                    <thead>
                      <tr>
                        <th>ภาค</th>
                        <th class="text-right">สถานศึกษา</th>
                        <th class="text-right">พนักงาน</th>
                        <th class="text-right">มีสิทธิ์</th>
                        <th class="text-right">ฐาน (บาท/ด.)</th>
                        <th class="text-right">วงเงิน {{ currentRun.budget_pct }}% (บาท/ปี)</th>
                        <th class="text-right">เลื่อนจริง (บาท/ปี)</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="r in byRegion" :key="r.region">
                        <td>{{ r.region }}</td>
                        <td class="text-right">{{ fmt(r.colleges) }}</td>
                        <td class="text-right">{{ fmt(r.personnel_count) }}</td>
                        <td class="text-right">{{ fmt(r.eligible_count) }}</td>
                        <td class="text-right">{{ fmt(r.total_salary) }}</td>
                        <td class="text-right">{{ fmt(r.budget_year) }}</td>
                        <td class="text-right font-weight-bold">{{ fmt(r.distributed_year) }}</td>
                      </tr>
                      <tr class="grey lighten-3 font-weight-bold">
                        <td>รวม</td>
                        <td class="text-right">{{ fmt(totals.processed) }}</td>
                        <td class="text-right">{{ fmt(totals.personnel_count) }}</td>
                        <td class="text-right">{{ fmt(totals.eligible_count) }}</td>
                        <td class="text-right">{{ fmt(totals.total_salary) }}</td>
                        <td class="text-right">{{ fmt(totals.budget_year) }}</td>
                        <td class="text-right">{{ fmt(totals.distributed_year) }}</td>
                      </tr>
                    </tbody>
                  </v-simple-table>
                </v-col>
                <v-col cols="12" md="5">
                  <div class="subtitle-2 font-weight-bold mb-1">แยกตามกลุ่มงาน</div>
                  <v-simple-table dense class="report-table">
                    <thead>
                      <tr>
                        <th>กลุ่มงาน</th>
                        <th class="text-right">คน</th>
                        <th class="text-right">มีสิทธิ์</th>
                        <th class="text-right">เลื่อน (บาท/ด.)</th>
                        <th class="text-right">ถึงขั้นสูง</th>
                        <th class="text-right">ตัดที่ 6%</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="g in summary.by_group" :key="g.name_type_group">
                        <td>{{ g.name_type_group || "-" }}</td>
                        <td class="text-right">{{ fmt(g.personnel) }}</td>
                        <td class="text-right">{{ fmt(g.eligible) }}</td>
                        <td class="text-right">{{ fmt(g.distributed_month) }}</td>
                        <td class="text-right">{{ fmt(g.capped) }}</td>
                        <td class="text-right">{{ fmt(g.pct_capped) }}</td>
                      </tr>
                    </tbody>
                  </v-simple-table>

                  <div class="subtitle-2 font-weight-bold mt-4 mb-1">สาเหตุที่ไม่มีสิทธิ์</div>
                  <v-simple-table dense class="report-table">
                    <tbody>
                      <tr v-for="r in summary.ineligible_reasons" :key="r.reason">
                        <td>{{ r.reason }}</td>
                        <td class="text-right">{{ fmt(r.n) }} คน</td>
                      </tr>
                      <tr v-if="!summary.ineligible_reasons.length">
                        <td class="grey--text">ไม่มี</td>
                      </tr>
                    </tbody>
                  </v-simple-table>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </template>
      </base-material-card>
    </v-container>

    <!-- ===== รายละเอียดรายบุคคลของสถานศึกษา ===== -->
    <v-dialog v-model="detailDialog" max-width="1300" scrollable>
      <v-card v-if="detailCollege">
        <v-card-title class="info darken-3 white--text py-2">
          <v-icon left dark>mdi-school</v-icon>
          {{ detailCollege.college_name }}
          <span class="caption ml-2">({{ detailCollege.college_code }})</span>
          <v-spacer />
          <v-btn icon dark @click="detailDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-card-text class="pt-3">
          <v-data-table
            :headers="detailHeaders"
            :items="detailRows"
            :loading="detailLoading"
            dense
            disable-pagination
            hide-default-footer
            fixed-header
            height="520px"
          >
            <template v-slot:item.full_name="{ item }">
              <div class="font-weight-medium">{{ item.full_name }}</div>
              <div class="caption grey--text">{{ item.name_postion }} · {{ item.name_type_group }}</div>
            </template>
            <template v-slot:item.performance_score="{ item }">
              <span v-if="+item.performance_score > 0">
                {{ item.performance_score }}
                <span class="caption grey--text">
                  (ร.{{ item.score_round }}{{ item.assessment_status !== "approved" ? " · " + (item.assessment_status || "-") : "" }})
                </span>
              </span>
              <span v-else class="grey--text">-</span>
            </template>
            <template v-slot:item.eligible="{ item }">
              <v-chip x-small dark :color="+item.eligible ? 'success' : 'error'">
                {{ +item.eligible ? "มีสิทธิ์" : "ไม่มีสิทธิ์" }}
              </v-chip>
              <div
                v-if="item.ineligible_reason"
                class="caption error--text"
                style="max-width:220px;white-space:normal"
              >
                {{ item.ineligible_reason }}
              </div>
            </template>
            <template v-slot:item.leave="{ item }">
              <span v-if="+item.has_attendance" class="caption">
                ลา {{ item.total_counted_leave }} · สาย {{ item.late_count }} · ขาด {{ item.absent_count }}
              </span>
              <span v-else class="caption grey--text">ไม่มีข้อมูล</span>
            </template>
            <template v-slot:item.salary_s="{ item }">{{ fmt(item.salary_s) }}</template>
            <template v-slot:item.actual_pct="{ item }">
              {{ +item.eligible ? Number(item.actual_pct).toFixed(3) + "%" : "-" }}
            </template>
            <template v-slot:item.final_rounded="{ item }">
              <span class="font-weight-bold primary--text">
                {{ +item.eligible ? fmt(item.final_rounded) : "-" }}
              </span>
              <div v-if="+item.capped" class="caption warning--text">ถึงขั้นสูง</div>
              <div v-if="+item.pct_capped" class="caption warning--text">ตัดที่ 6%</div>
            </template>
            <template v-slot:item.new_salary="{ item }">
              <span class="font-weight-bold">{{ fmt(item.new_salary) }}</span>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" top :timeout="5000">
      {{ snackbar.text }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.show = false">ปิด</v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import * as XLSX from "xlsx";
import { calculateCollege } from "@/utils/govemCompensationEngine";

const API = "govem_compensation_central.php";
const CONCURRENCY = 3; // จำนวนสถานศึกษาที่ประมวลผลพร้อมกัน

export default {
  name: "CentralCompensation",

  data() {
    const thisYear = new Date().getFullYear() + 543;
    return {
      ApiKey: "HRvec2021",
      years: [thisYear - 2, thisYear - 1, thisYear, thisYear + 1],
      compYear: thisYear,
      overview: null,
      runs: [],
      currentRun: null,
      colleges: [],
      summary: null,
      busy: false,
      processing: false,
      stopRequested: false,
      exporting: false,
      collegeSearch: "",
      collegeFilter: "all",
      newRun: { round_mode: "latest", salary_source: "pt", run_note: "" },
      roundModeItems: [
        { text: "รอบล่าสุด (รอบ 2 ถ้ามี ไม่มีใช้รอบ 1)", value: "latest" },
        { text: "รอบที่ 1", value: "1" },
        { text: "รอบที่ 2 (เหมือนค่าเริ่มต้นสถานศึกษา)", value: "2" }
      ],
      salarySourceItems: [
        { text: "ทะเบียนประวัติ (เหมือนสถานศึกษา)", value: "pt" },
        { text: "กรอบอัตรากำลัง (government_employee)", value: "ge" }
      ],
      detailDialog: false,
      detailLoading: false,
      detailCollege: null,
      detailRows: [],
      snackbar: { show: false, color: "success", text: "" },
      runHeaders: [
        { text: "รอบ", value: "run_id", width: 60 },
        { text: "ผลประเมิน", value: "round_mode" },
        { text: "ฐานค่าตอบแทน", value: "salary_source" },
        { text: "สถานะ", value: "run_status" },
        { text: "ประมวลผลแล้ว (แห่ง)", value: "processed_colleges" },
        { text: "หมายเหตุ", value: "run_note" },
        { text: "สร้างเมื่อ", value: "created_at" },
        { text: "", value: "actions", sortable: false, align: "right" }
      ],
      collegeHeaders: [
        { text: "สถานศึกษา", value: "college_name" },
        { text: "ภาค", value: "region_name" },
        { text: "สถานะ", value: "state", width: 180 },
        { text: "มีสิทธิ์/ทั้งหมด", value: "eligible_count", align: "center" },
        { text: "วงเงิน 4% (บาท/ปี)", value: "budget_year", align: "right" },
        { text: "เลื่อนจริง (บาท/ปี)", value: "distributed_year", align: "right" },
        { text: "", value: "actions", sortable: false, width: 80 },
        // ใช้ค้นหาเท่านั้น
        { text: "", value: "province_name", align: " d-none" },
        { text: "", value: "college_code", align: " d-none" }
      ],
      detailHeaders: [
        { text: "ชื่อ-สกุล", value: "full_name" },
        { text: "ค่าตอบแทน", value: "salary_s", align: "right" },
        { text: "คะแนน", value: "performance_score" },
        { text: "การลา", value: "leave", sortable: false },
        { text: "สิทธิ์", value: "eligible" },
        { text: "% เลื่อนจริง", value: "actual_pct", align: "center" },
        { text: "เลื่อน (บาท/ด.)", value: "final_rounded", align: "right" },
        { text: "ค่าตอบแทนใหม่", value: "new_salary", align: "right" }
      ]
    };
  },

  computed: {
    isFinal() {
      return this.currentRun && this.currentRun.run_status === "final";
    },
    doneCount() {
      return this.colleges.filter(c => c.state === "ok").length;
    },
    errorCount() {
      return this.colleges.filter(c => c.state === "error").length;
    },
    pendingOrErrorCount() {
      return this.colleges.filter(c => c.state !== "ok").length;
    },
    progressPct() {
      return this.colleges.length ? (this.doneCount / this.colleges.length) * 100 : 0;
    },
    filteredColleges() {
      switch (this.collegeFilter) {
        case "pending":
          return this.colleges.filter(c => c.state === "pending");
        case "error":
          return this.colleges.filter(c => c.state === "error");
        case "incomplete":
          return this.colleges.filter(
            c => c.state === "ok" && (+c.no_score_count > 0 || +c.data_issue_count > 0)
          );
        default:
          return this.colleges;
      }
    },
    totals() {
      const rows = (this.summary && this.summary.colleges) || [];
      const sum = k => rows.reduce((s, r) => s + (+r[k] || 0), 0);
      return {
        processed: rows.length,
        personnel_count: sum("personnel_count"),
        eligible_count: sum("eligible_count"),
        no_score_count: sum("no_score_count"),
        unapproved_score_count: sum("unapproved_score_count"),
        no_attendance_count: sum("no_attendance_count"),
        data_issue_count: sum("data_issue_count"),
        capped_count: sum("capped_count"),
        pct_capped_count: sum("pct_capped_count"),
        total_salary: sum("total_salary"),
        budget_year: sum("budget_year"),
        distributed_month: sum("distributed_month"),
        distributed_year: sum("distributed_year"),
        no_score_colleges: rows.filter(r => +r.no_score_count > 0).length
      };
    },
    budgetUsePct() {
      return this.totals.budget_year
        ? (this.totals.distributed_year / this.totals.budget_year) * 100
        : 0;
    },
    kpis() {
      const t = this.totals;
      return [
        { label: "สถานศึกษา", value: this.fmt(t.processed), unit: `จาก ${this.fmt(this.summary.expected_colleges)} แห่ง`, color: "#455a64" },
        { label: "พนักงานราชการ", value: this.fmt(t.personnel_count), unit: `มีสิทธิ์ ${this.fmt(t.eligible_count)} คน`, color: "#1565c0" },
        { label: "ฐานค่าตอบแทนรวม", value: this.fmt(t.total_salary), unit: "บาท/เดือน", color: "#2e7d32" },
        { label: `วงเงิน ${this.currentRun.budget_pct}% ที่ใช้ได้`, value: this.fmt(t.budget_year), unit: "บาท/ปี", color: "#ef6c00" },
        { label: "ยอดเลื่อนจริง", value: this.fmt(t.distributed_month), unit: "บาท/เดือน", color: "#6a1b9a" },
        { label: "ยอดเลื่อนจริง", value: this.fmt(t.distributed_year), unit: "บาท/ปี (×12)", color: "#6a1b9a" },
        { label: "วงเงินคงเหลือ", value: this.fmt(t.budget_year - t.distributed_year), unit: "บาท/ปี", color: "#00838f" },
        { label: "ค่าตอบแทนใหม่รวม", value: this.fmt(t.total_salary + t.distributed_month), unit: "บาท/เดือน", color: "#37474f" }
      ];
    },
    quality() {
      const t = this.totals;
      return [
        { label: "ไม่มีผลประเมิน", value: t.no_score_count, hint: `${this.fmt(t.no_score_colleges)} แห่ง`, color: "error--text", bg: "red lighten-5" },
        { label: "ผลประเมินยังไม่อนุมัติ", value: t.unapproved_score_count, hint: "นับรวมในการคำนวณ", color: "warning--text", bg: "orange lighten-5" },
        { label: "ไม่มีข้อมูลการลา", value: t.no_attendance_count, hint: "ถือว่าไม่มีวันลา", color: "warning--text", bg: "orange lighten-5" },
        { label: "ข้อมูลค่าตอบแทนผิดปกติ", value: t.data_issue_count, hint: "0 หรือ ≥ ขั้นสูง", color: "error--text", bg: "red lighten-5" },
        { label: "ถึงขั้นสูงกลุ่มงาน", value: t.capped_count, hint: "ข้อ 1(4)", color: "info--text", bg: "blue lighten-5" },
        { label: "ตัดที่เพดาน 6%", value: t.pct_capped_count, hint: "ข้อ 1(2)", color: "info--text", bg: "blue lighten-5" }
      ];
    },
    byRegion() {
      const map = {};
      ((this.summary && this.summary.colleges) || []).forEach(c => {
        const key = c.region_name || "ไม่ระบุภาค";
        const r = (map[key] = map[key] || {
          region: key, order: +c.region_ID || 99, colleges: 0, personnel_count: 0,
          eligible_count: 0, total_salary: 0, budget_year: 0, distributed_year: 0
        });
        r.colleges++;
        ["personnel_count", "eligible_count", "total_salary", "budget_year", "distributed_year"]
          .forEach(k => (r[k] += +c[k] || 0));
      });
      return Object.values(map).sort((a, b) => a.order - b.order);
    }
  },

  mounted() {
    this.loadOverview();
  },

  methods: {
    fmt(v) {
      if (v === null || v === undefined || isNaN(v)) return "-";
      return Number(v).toLocaleString("th-TH", { maximumFractionDigits: 0 });
    },
    notify(text, color = "success") {
      this.snackbar = { show: true, text, color };
    },
    roundStat(r) {
      const s = this.overview && this.overview.rounds.find(x => +x.assessment_round === r);
      return s ? { total: +s.total, approved: +s.approved, colleges: +s.colleges } : { total: 0, approved: 0, colleges: 0 };
    },
    roundModeText(v) {
      return { latest: "รอบล่าสุด", 1: "รอบที่ 1", 2: "รอบที่ 2" }[v] || v;
    },
    salarySourceText(v) {
      return v === "ge" ? "ฐาน: กรอบอัตรากำลัง" : "ฐาน: ทะเบียนประวัติ";
    },
    stateText(s) {
      return { pending: "รอประมวลผล", processing: "กำลังประมวลผล", ok: "เสร็จ", error: "ผิดพลาด" }[s] || s;
    },
    stateColor(s) {
      return { pending: "grey", processing: "info", ok: "success", error: "error" }[s] || "grey";
    },

    async api(payload) {
      try {
        const res = await this.$http.post(API, { ApiKey: this.ApiKey, ...payload });
        if (!res.data || res.data.status !== true) {
          throw new Error((res.data && res.data.message) || "ไม่ได้รับข้อมูลจากเซิร์ฟเวอร์");
        }
        return res.data;
      } catch (err) {
        const msg = err.response && err.response.data && err.response.data.message;
        throw new Error(msg || err.message);
      }
    },

    async loadOverview() {
      this.currentRun = null;
      this.summary = null;
      this.colleges = [];
      try {
        const data = await this.api({ action: "overview", comp_year: this.compYear });
        this.overview = data;
        this.runs = data.runs;
      } catch (e) {
        this.notify(e.message, "error");
      }
    },

    async createRun() {
      this.busy = true;
      try {
        const data = await this.api({ action: "create_run", comp_year: this.compYear, ...this.newRun });
        this.newRun.run_note = "";
        await this.loadOverview();
        await this.selectRun(data.run);
        this.notify(`สร้างรอบ #${data.run.run_id} แล้ว — กด "ประมวลผลทุกสถานศึกษา" เพื่อเริ่ม`);
      } catch (e) {
        this.notify(e.message, "error");
      } finally {
        this.busy = false;
      }
    },

    async deleteRun(run) {
      if (!confirm(`ลบรอบ #${run.run_id} และผลการประมวลผลทั้งหมดของรอบนี้?`)) return;
      try {
        await this.api({ action: "delete_run", run_id: run.run_id });
        await this.loadOverview();
        this.notify(`ลบรอบ #${run.run_id} แล้ว`, "info");
      } catch (e) {
        this.notify(e.message, "error");
      }
    },

    async selectRun(run) {
      try {
        const data = await this.api({ action: "colleges", run_id: run.run_id });
        this.currentRun = data.run;
        this.colleges = data.colleges.map(c => ({
          no_score_count: 0,
          data_issue_count: 0,
          ...c,
          state: c.process_status ? (c.process_status === "error" ? "error" : "ok") : "pending"
        }));
        await this.loadSummary();
      } catch (e) {
        this.notify(e.message, "error");
      }
    },

    async loadSummary() {
      if (!this.currentRun) return;
      try {
        const data = await this.api({ action: "summary", run_id: this.currentRun.run_id });
        this.summary = data;
        this.currentRun = data.run;
        // ซิงก์ตัวเลขในคิวกับผลที่บันทึกจริง
        const byCode = {};
        data.colleges.forEach(c => (byCode[c.college_code] = c));
        this.colleges.forEach(c => {
          const s = byCode[c.college_code];
          if (s) Object.assign(c, s, { state: s.process_status === "error" ? "error" : "ok" });
        });
      } catch (e) {
        this.notify(e.message, "error");
      }
    },

    // ประมวลผลตามคิว ทีละ CONCURRENCY แห่ง
    async processColleges(list) {
      if (this.isFinal || this.processing || !list.length) return;
      this.processing = true;
      this.stopRequested = false;
      const queue = list.slice();
      list.forEach(c => {
        c.state = "pending";
        c.process_message = "";
      });
      const worker = async () => {
        while (queue.length && !this.stopRequested) {
          await this.processOne(queue.shift());
        }
      };
      await Promise.all(Array.from({ length: CONCURRENCY }, worker));
      this.processing = false;
      await this.loadSummary();
      const failed = list.filter(c => c.state === "error").length;
      if (this.stopRequested) this.notify("หยุดการประมวลผลแล้ว", "warning");
      else if (failed) this.notify(`ประมวลผลเสร็จ ผิดพลาด ${failed} แห่ง — กด "เฉพาะที่ค้าง/ผิดพลาด" เพื่อลองใหม่`, "warning");
      else this.notify(`ประมวลผลเสร็จ ${list.length} แห่ง`);
    },

    async processOne(college) {
      const run = this.currentRun;
      college.state = "processing";
      try {
        const data = await this.api({ action: "college_data", run_id: run.run_id, college_code: college.college_code });
        const { records } = calculateCollege(data.employees, {
          budgetPct: +run.budget_pct,
          maxPct: +run.max_pct,
          roundMode: run.round_mode
        });
        const saved = await this.api({
          action: "save_college",
          run_id: run.run_id,
          college_code: college.college_code,
          details: records
        });
        Object.assign(college, {
          state: "ok",
          personnel_count: saved.personnel,
          eligible_count: saved.eligible,
          budget_year: saved.budget_year,
          distributed_year: saved.distributed_year,
          process_message: saved.eligible === 0 ? "ไม่มีผู้มีสิทธิ์รับการเลื่อน" : ""
        });
      } catch (e) {
        college.state = "error";
        college.process_message = e.message;
      }
    },

    async finalizeRun() {
      if (!confirm("ยืนยันผลรอบนี้? หลังยืนยันจะไม่สามารถประมวลผลใหม่ได้จนกว่าจะยกเลิกการยืนยัน")) return;
      try {
        await this.api({ action: "finalize", run_id: this.currentRun.run_id });
        await this.loadSummary();
        this.runs = (await this.api({ action: "overview", comp_year: this.compYear })).runs;
        this.notify("ยืนยันผลเรียบร้อย");
      } catch (e) {
        this.notify(e.message, "error");
      }
    },

    async reopenRun() {
      if (!confirm("ยกเลิกการยืนยันผลรอบนี้เพื่อประมวลผลใหม่?")) return;
      try {
        await this.api({ action: "reopen", run_id: this.currentRun.run_id });
        await this.loadSummary();
        this.runs = (await this.api({ action: "overview", comp_year: this.compYear })).runs;
        this.notify("ยกเลิกการยืนยันแล้ว", "info");
      } catch (e) {
        this.notify(e.message, "error");
      }
    },

    async openCollegeDetail(college) {
      this.detailCollege = college;
      this.detailRows = [];
      this.detailDialog = true;
      this.detailLoading = true;
      try {
        const data = await this.api({
          action: "details",
          run_id: this.currentRun.run_id,
          college_code: college.college_code
        });
        this.detailRows = data.details;
      } catch (e) {
        this.notify(e.message, "error");
      } finally {
        this.detailLoading = false;
      }
    },

    reportTitle() {
      const r = this.currentRun;
      return {
        title: "สรุปการเลื่อนค่าตอบแทนพนักงานราชการ สังกัดสำนักงานคณะกรรมการการอาชีวศึกษา",
        sub: `ผลการประเมินปีงบประมาณ ${r.comp_year} (${this.roundModeText(r.round_mode)}) · มีผล 1 ตุลาคม ${r.comp_year}` +
          ` · วงเงินไม่เกิน ${r.budget_pct}% · ${this.salarySourceText(r.salary_source)}` +
          ` · รอบประมวลผล #${r.run_id} ${r.run_status === "final" ? "(ยืนยันผล " + r.finalized_at + ")" : "(ฉบับร่าง)"}`
      };
    },

    async exportExcel() {
      this.exporting = true;
      try {
        const data = await this.api({ action: "details", run_id: this.currentRun.run_id });
        const t = this.totals;
        const head = this.reportTitle();
        const wb = XLSX.utils.book_new();

        const summaryRows = [
          [head.title], [head.sub], [],
          ["รายการ", "จำนวน", "หน่วย"],
          ["สถานศึกษาที่ประมวลผล", t.processed, "แห่ง"],
          ["พนักงานราชการทั้งหมด", t.personnel_count, "คน"],
          ["ผู้มีสิทธิ์รับการเลื่อน", t.eligible_count, "คน"],
          ["ฐานค่าตอบแทนรวม", t.total_salary, "บาท/เดือน"],
          [`วงเงิน ${this.currentRun.budget_pct}% ที่ใช้ได้`, t.budget_year, "บาท/ปี"],
          ["ยอดเลื่อนค่าตอบแทนจริง", t.distributed_month, "บาท/เดือน"],
          ["ยอดเลื่อนค่าตอบแทนจริง", t.distributed_year, "บาท/ปี"],
          ["วงเงินคงเหลือ", t.budget_year - t.distributed_year, "บาท/ปี"],
          ["ไม่มีผลประเมิน", t.no_score_count, "คน"],
          ["ข้อมูลค่าตอบแทนผิดปกติ", t.data_issue_count, "คน"],
          [], ["ภาค", "สถานศึกษา", "พนักงาน", "มีสิทธิ์", "ฐาน (บาท/เดือน)", "วงเงิน (บาท/ปี)", "เลื่อนจริง (บาท/ปี)"],
          ...this.byRegion.map(r => [r.region, r.colleges, r.personnel_count, r.eligible_count, r.total_salary, r.budget_year, r.distributed_year])
        ];
        XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(summaryRows), "สรุป");

        const collegeRows = this.summary.colleges.map((c, i) => ({
          ลำดับ: i + 1,
          รหัส: c.college_code,
          สถานศึกษา: c.college_name,
          จังหวัด: c.province_name,
          ภาค: c.region_name,
          พนักงาน: +c.personnel_count,
          มีสิทธิ์: +c.eligible_count,
          ไม่มีผลประเมิน: +c.no_score_count,
          "ฐานค่าตอบแทน (บาท/เดือน)": +c.total_salary,
          [`วงเงิน ${this.currentRun.budget_pct}% (บาท/ปี)`]: +c.budget_year,
          "เลื่อนจริง (บาท/เดือน)": +c.distributed_month,
          "เลื่อนจริง (บาท/ปี)": +c.distributed_year,
          "คงเหลือ (บาท/ปี)": +c.budget_year - +c.distributed_year,
          "สถานศึกษาอนุมัติเองแล้ว": +c.college_approved ? "ใช่" : ""
        }));
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(collegeRows), "รายสถานศึกษา");

        const detailRows = data.details.map((d, i) => ({
          ลำดับ: i + 1,
          รหัสสถานศึกษา: d.college_code,
          สถานศึกษา: d.college_name,
          เลขบัตรประชาชน: d.id_card,
          "ชื่อ-สกุล": d.full_name,
          ตำแหน่ง: d.name_postion,
          กลุ่มงาน: d.name_type_group,
          ค่าตอบแทนเดิม: +d.salary_s,
          คะแนนประเมิน: +d.performance_score,
          รอบประเมิน: d.score_round || "",
          สถานะผลประเมิน: d.assessment_status || "",
          สิทธิ์: +d.eligible ? "มีสิทธิ์" : "ไม่มีสิทธิ์",
          หมายเหตุ: d.ineligible_reason || "",
          "% รายบุคคล": +d.individual_pct,
          "% บริหารวงเงิน": +d.management_pct,
          "% เลื่อนจริง": +d.actual_pct,
          "เลื่อนจริง (บาท/เดือน)": +d.final_rounded,
          ค่าตอบแทนใหม่: +d.new_salary,
          ถึงขั้นสูง: +d.capped ? "ใช่" : "",
          ตัดที่เพดาน6: +d.pct_capped ? "ใช่" : ""
        }));
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(detailRows), "รายบุคคล");

        XLSX.writeFile(wb, `เลื่อนค่าตอบแทนพนักงานราชการ_${this.currentRun.comp_year}_รอบ${this.currentRun.run_id}.xlsx`);
      } catch (e) {
        this.notify("ส่งออกไม่สำเร็จ: " + e.message, "error");
      } finally {
        this.exporting = false;
      }
    },

    // รายงานสำหรับพิมพ์/บันทึก PDF เสนอสำนักงบประมาณ (สรุปรายภาค + รายสถานศึกษา)
    printReport() {
      const esc = s => String(s === null || s === undefined ? "" : s)
        .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
      const t = this.totals;
      const head = this.reportTitle();
      const n = v => this.fmt(v);
      const regionRows = this.byRegion.map(r =>
        `<tr><td>${esc(r.region)}</td><td>${n(r.colleges)}</td><td>${n(r.personnel_count)}</td><td>${n(r.eligible_count)}</td>` +
        `<td>${n(r.total_salary)}</td><td>${n(r.budget_year)}</td><td>${n(r.distributed_year)}</td></tr>`).join("");
      const collegeRows = this.summary.colleges.map((c, i) =>
        `<tr><td>${i + 1}</td><td class="l">${esc(c.college_name)}</td><td class="l">${esc(c.province_name)}</td>` +
        `<td>${n(c.personnel_count)}</td><td>${n(c.eligible_count)}</td><td>${n(c.total_salary)}</td>` +
        `<td>${n(c.budget_year)}</td><td>${n(c.distributed_month)}</td><td>${n(c.distributed_year)}</td></tr>`).join("");
      const html = `<!doctype html><html><head><meta charset="utf-8"><title>${esc(head.title)}</title>
        <style>
          body{font-family:"TH Sarabun New","Sarabun",sans-serif;font-size:15px;margin:24px;color:#000}
          h2{text-align:center;margin:0 0 4px;font-size:20px} .sub{text-align:center;margin-bottom:12px}
          table{border-collapse:collapse;width:100%;margin-bottom:16px} th,td{border:1px solid #444;padding:2px 6px;text-align:right}
          th{background:#eee;text-align:center} td.l{text-align:left} tfoot td{font-weight:bold;background:#f5f5f5}
          .sign{margin-top:40px;width:45%;margin-left:auto;text-align:center;line-height:2}
          @media print{thead{display:table-header-group}}
        </style></head><body>
        <h2>${esc(head.title)}</h2><div class="sub">${esc(head.sub)}</div>
        <table><thead><tr><th>ภาค</th><th>สถานศึกษา</th><th>พนักงาน</th><th>มีสิทธิ์</th><th>ฐานค่าตอบแทน (บาท/เดือน)</th>
          <th>วงเงิน ${esc(this.currentRun.budget_pct)}% (บาท/ปี)</th><th>เลื่อนจริง (บาท/ปี)</th></tr></thead>
          <tbody>${regionRows}</tbody>
          <tfoot><tr><td class="l">รวมทั้งสิ้น</td><td>${n(t.processed)}</td><td>${n(t.personnel_count)}</td><td>${n(t.eligible_count)}</td>
          <td>${n(t.total_salary)}</td><td>${n(t.budget_year)}</td><td>${n(t.distributed_year)}</td></tr></tfoot></table>
        <table><thead><tr><th>ที่</th><th>สถานศึกษา</th><th>จังหวัด</th><th>พนักงาน</th><th>มีสิทธิ์</th><th>ฐาน (บาท/เดือน)</th>
          <th>วงเงิน (บาท/ปี)</th><th>เลื่อนจริง (บาท/เดือน)</th><th>เลื่อนจริง (บาท/ปี)</th></tr></thead>
          <tbody>${collegeRows}</tbody>
          <tfoot><tr><td></td><td class="l">รวม</td><td></td><td>${n(t.personnel_count)}</td><td>${n(t.eligible_count)}</td>
          <td>${n(t.total_salary)}</td><td>${n(t.budget_year)}</td><td>${n(t.distributed_month)}</td><td>${n(t.distributed_year)}</td></tr></tfoot></table>
        <div>หมายเหตุ: ไม่มีผลการประเมิน ${n(t.no_score_count)} คน · ข้อมูลค่าตอบแทนผิดปกติ ${n(t.data_issue_count)} คน ·
          ถึงขั้นสูงกลุ่มงาน ${n(t.capped_count)} คน · ตัดที่เพดาน 6% ${n(t.pct_capped_count)} คน</div>
        <div class="sign">(ลงชื่อ)......................................................<br>(......................................................)<br>ผู้จัดทำรายงาน</div>
        </body></html>`;
      const w = window.open("", "_blank");
      if (!w) {
        this.notify("เบราว์เซอร์บล็อกหน้าต่างใหม่ กรุณาอนุญาต pop-up", "warning");
        return;
      }
      w.document.write(html);
      w.document.close();
      w.focus();
      w.print();
    }
  }
};
</script>

<style scoped>
.kpi {
  background: #fff;
  border-radius: 8px;
  padding: 12px;
  text-align: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.07);
  border-left: 4px solid #455a64;
}
.kpi-label {
  font-size: 12px;
  font-weight: 600;
  color: #777;
}
.kpi-value {
  font-size: 20px;
  font-weight: bold;
  margin: 4px 0;
}
.kpi-unit {
  font-size: 11px;
  color: #999;
}
.report-table >>> th,
.report-table >>> td {
  font-size: 12px !important;
}
</style>
